import type { AxiosResponse } from 'axios'
import http from './request'
import type {
  AdminUserVO,
  ApiResponse,
  LoginParams,
  LoginVO,
  PageQuery,
  PageResult,
  ProfileVO,
  PublicConfigVO,
  TokenVO,
  UserDetailVO,
  UserVO,
} from './types'

const BASE = '/v1/admin'

/** 后端 GET /admin/users：keyword + status + role(1普通/2管理员) */
export interface UserListQuery extends PageQuery {
  keyword?: string
  status?: 1 | 2 | 3
  /** 1 普通用户 2 管理员 */
  role?: 1 | 2
}

/** 登录：后端返回 { token, user }，这里只取令牌 */
export async function login(data: LoginParams): Promise<TokenVO> {
  // remember 仅前端使用，不下发给后端
  const payload: LoginParams = { username: data.username, password: data.password }
  if (data.captchaToken) payload.captchaToken = data.captchaToken
  if (data.captchaCode) payload.captchaCode = data.captchaCode
  const res = await http.post<LoginVO>('/v1/auth/login', payload, {
    withToken: false,
    preventDuplicate: true,
  })
  return res?.token ?? (res as unknown as TokenVO)
}

/** 公开配置：含 captchaEnabled，决定登录页是否展示验证码 */
export function fetchPublicConfig() {
  return http.get<PublicConfigVO>('/v1/auth/public-config', undefined, {
    withToken: false,
    loading: false,
    silent: true,
  })
}

/** 验证码解析结果 */
export interface CaptchaResult {
  captchaId: string
  /** 可直接绑定到 img src 的地址（data URL / objectURL） */
  imageUrl: string
}

/** base64 → data URL，兼容已带前缀 / 远程地址 / objectURL */
function normalizeImageSrc(raw: string): string {
  const value = raw.trim()
  if (!value) return ''
  if (value.startsWith('data:') || value.startsWith('http') || value.startsWith('blob:')) {
    return value
  }
  return `data:image/png;base64,${value.replace(/\s+/g, '')}`
}

/**
 * 图形验证码：兼容后端三种返回格式
 * 1) 统一信封 { code, data: { captchaId, imageBase64 } }
 * 2) 裸 JSON { captchaId, imageBase64 }
 * 3) 图片二进制流（captchaId 放在 X-Captcha-Id 响应头）
 */
export async function fetchCaptcha(): Promise<CaptchaResult> {
  const response = await http.get<AxiosResponse<unknown>>('/v1/auth/captcha', undefined, {
    withToken: false,
    loading: false,
    silent: true,
    cancelable: false,
    raw: true,
  })

  const headers = response.headers ?? {}
  const contentType = String(headers['content-type'] ?? '')

  // 3) 二进制图片流
  if (response.data instanceof Blob || contentType.startsWith('image/')) {
    const blob =
      response.data instanceof Blob
        ? response.data
        : new Blob([response.data as BlobPart], { type: contentType || 'image/png' })
    const captchaId = String(headers['x-captcha-id'] ?? headers['captcha-id'] ?? '')
    return { captchaId, imageUrl: URL.createObjectURL(blob) }
  }

  // 1) / 2) JSON
  const body = (response.data ?? {}) as Partial<ApiResponse<Record<string, unknown>>> &
    Record<string, unknown>
  const payload = (
    body && typeof body === 'object' && 'data' in body && body.data
      ? (body.data as Record<string, unknown>)
      : body
  ) as Record<string, unknown>

  const captchaId = String(
    payload.captchaId ?? payload.captcha_id ?? payload.token ?? payload.id ?? payload.key ?? ''
  )
  const imageUrl = normalizeImageSrc(
    String(payload.imageBase64 ?? payload.image ?? payload.imageBase ?? payload.base64 ?? '')
  )
  if (!imageUrl) throw new Error('CAPTCHA_IMAGE_MISSING')
  return { captchaId, imageUrl }
}

/** 退出登录 */
export function logout() {
  return http.post<void>('/v1/auth/logout', undefined, { loading: false })
}

/** 当前管理员信息：GET /auth/me → UserVO，角色由 userType 推导 */
export async function fetchProfile(): Promise<ProfileVO> {
  const user = await http.get<UserVO>('/v1/auth/me')
  return {
    id: user.id,
    username: user.username,
    nickname: user.nickname,
    avatarUrl: user.avatarUrl,
    userType: user.userType,
    roles: user.userType === 2 ? ['ADMIN'] : [],
    permissions: [],
    lastLoginAt: user.lastLoginAt,
    createdAt: user.createdAt,
    usingDefaultPassword: user.usingDefaultPassword,
  }
}

/** 修改个人资料：PUT /auth/me（后端仅支持 nickname + avatarUrl） */
export function updateProfile(data: { nickname: string; avatarUrl: string }) {
  return http.put<UserVO>('/v1/auth/me', data, { preventDuplicate: true })
}

/** 修改密码：PUT /auth/me/password */
export function changePassword(data: { oldPassword: string; newPassword: string }) {
  return http.put<void>('/v1/auth/me/password', data, { preventDuplicate: true })
}

/** 用户列表 */
export function fetchUserList(params: UserListQuery) {
  return http.get<PageResult<AdminUserVO>>(`${BASE}/users`, params)
}

/** 用户详情：后端 GET /admin/users/{id} 只返回 UserVO */
export async function fetchUserDetail(id: number): Promise<UserDetailVO> {
  const user = await http.get<AdminUserVO>(`${BASE}/users/${id}`)
  return { user }
}

/** 修改用户状态（1 启用 / 2 禁用，禁用即踢线）：status 走 query */
export function updateUserStatus(id: number, status: 1 | 2) {
  return http.put<UserVO>(`${BASE}/users/${id}/status`, undefined, {
    params: { status },
    preventDuplicate: true,
  })
}

/** 修改用户角色：role 走 query，1 普通用户 2 管理员 */
export function updateUserRole(id: number, role: 1 | 2) {
  return http.put<UserVO>(`${BASE}/users/${id}/role`, undefined, {
    params: { role },
    preventDuplicate: true,
  })
}

/** 删除用户 */
export function deleteUser(id: number) {
  return http.delete<void>(`${BASE}/users/${id}`, undefined, { preventDuplicate: true })
}
